import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, eur } from "@/lib/api";
import { Users } from "lucide-react";

function Row({ label, value, testid, className, strong }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-sm uppercase tracking-wide text-stone-500">{label}</span>
      <span data-testid={testid} className={`${strong ? "font-serif-display text-xl font-bold" : "font-semibold"} ${className || "text-stone-100"}`}>{value}</span>
    </div>
  );
}

export default function TableOverviewDrawer({ open, onClose, slug, tableNumber, sessionId, orders, onOpenOrder }) {
  const [bill, setBill] = useState(null);
  useEffect(() => {
    if (open && slug && tableNumber) api.getPublicBill(slug, tableNumber).then(setBill).catch(() => setBill(null));
  }, [open, slug, tableNumber]);
  if (!open) return null;

  const sessionOrders = orders.filter(
    (o) => String(o.table_number) === String(tableNumber) && (sessionId ? o.table_session_id === sessionId : true) && o.status !== "REFUSEE"
  );
  const groups = {};
  sessionOrders.forEach((o) => {
    const g = o.guest_name || "Invité";
    (groups[g] = groups[g] || []).push(o);
  });
  const total = Math.round(sessionOrders.reduce((s, o) => s + (o.total || 0), 0) * 100) / 100;
  const paid = bill?.paid_amount || 0;
  const remaining = Math.max(Math.round((total - paid) * 100) / 100, 0);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" data-testid="table-overview-drawer" className="w-full sm:max-w-md bg-[#0C1713] border-l border-[#1B3028] text-stone-100 p-0 flex flex-col">
        <SheetHeader className="p-5 border-b border-[#1B3028] text-left">
          <SheetTitle className="font-serif-display text-3xl text-stone-100">Table {tableNumber}</SheetTitle>
          <SheetDescription className="text-stone-400">Session en cours · {sessionOrders.length} commande(s) · {Object.keys(groups).length} client(s)</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {Object.entries(groups).map(([g, list], gi) => (
            <div key={g}>
              <p className="text-xs uppercase tracking-widest text-[#C69C4A] font-bold mb-2 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> Client {gi + 1} · {g}
              </p>
              <div className="space-y-2">
                {list.map((o) => (
                  <button
                    key={o.id}
                    data-testid={`table-order-${o.order_number.replace("#", "")}`}
                    onClick={() => onOpenOrder(o)}
                    className="w-full text-left rounded-xl bg-[#111F1A] border border-[#1B3028] p-3 hover:border-[#C69C4A]/50 transition-colors"
                  >
                    <div className="flex justify-between">
                      <span className="font-semibold text-stone-100">{o.order_code || o.order_number}</span>
                      <span className="text-[#C69C4A] font-bold">{eur(o.total)}</span>
                    </div>
                    <p className="text-xs text-stone-400 mt-0.5 truncate">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(" · ")}</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {sessionOrders.length === 0 && <p className="text-center text-stone-500 text-sm py-8">Aucune commande active pour cette table.</p>}
        </div>

        <div className="border-t border-[#1B3028] p-5 space-y-2.5 shrink-0">
          <Row label="Total table" value={eur(total)} testid="table-total" strong />
          <Row label="Payé" value={eur(paid)} testid="table-paid" className="text-emerald-300" />
          <Row label="Reste" value={eur(remaining)} testid="table-remaining" className="text-[#E7C97A]" strong />
        </div>
      </SheetContent>
    </Sheet>
  );
}
